import {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";

export type ShopFilterCategory = {
  id: number;
  name: string;
  children?: ShopFilterCategory[];
};

type Props = {
  categories: ShopFilterCategory[];
  isLoading: boolean;
  hasFailed?: boolean;
  selectedId: string | null;
  onSelect: (categoryId: string | null) => void;
  eyebrow: string;
  title: string;
  allLabel: string;
  /** Rendered inside the chip; receives the category so callers can map icons by name. */
  renderIcon: (category: ShopFilterCategory | null) => ReactNode;
};

const SCROLL_STEP = 320;

export function findCategoryPath(
  categories: ShopFilterCategory[],
  selectedId: string | null
): ShopFilterCategory[] {
  if (!selectedId) return [];

  for (const category of categories) {
    if (String(category.id) === selectedId) return [category];
    const childPath = findCategoryPath(category.children ?? [], selectedId);
    if (childPath.length > 0) return [category, ...childPath];
  }

  return [];
}

function resolveBrowsePath(
  categories: ShopFilterCategory[],
  ids: number[]
): ShopFilterCategory[] {
  const path: ShopFilterCategory[] = [];
  let level = categories;

  for (const id of ids) {
    const node = level.find((category) => category.id === id);
    if (!node) break;
    path.push(node);
    level = node.children ?? [];
  }

  return path;
}

/**
 * Scrollable category strip shared by the product and service listings, so both
 * pages filter through the same UI.
 */
const ShopCategoryFilter = ({
  categories,
  isLoading,
  hasFailed = false,
  selectedId,
  onSelect,
  eyebrow,
  title,
  allLabel,
  renderIcon,
}: Props) => {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  // Callback ref, not useRef: the track only mounts once the categories have
  // loaded, so an effect keyed on a ref would never see it and would leave the
  // arrows stuck at their initial state.
  const [track, setTrack] = useState<HTMLDivElement | null>(null);
  const [canPrevious, setCanPrevious] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [browsePathIds, setBrowsePathIds] = useState<number[]>([]);

  const selectedPath = useMemo(
    () => findCategoryPath(categories, selectedId),
    [categories, selectedId]
  );

  useEffect(() => {
    if (!selectedId || selectedPath.length === 0) {
      setBrowsePathIds([]);
      return;
    }

    const selected = selectedPath[selectedPath.length - 1];
    const browsePath =
      (selected.children?.length ?? 0) > 0
        ? selectedPath
        : selectedPath.slice(0, -1);
    setBrowsePathIds(browsePath.map((category) => category.id));
  }, [selectedId, selectedPath]);

  const browsePath = useMemo(
    () => resolveBrowsePath(categories, browsePathIds),
    [browsePathIds, categories]
  );
  const currentParent = browsePath[browsePath.length - 1] ?? null;
  const visibleCategories = currentParent?.children ?? categories;
  const levelKey = browsePathIds.join("-") || "root";
  const scopeSelected = currentParent
    ? selectedId === String(currentParent.id)
    : !selectedId;

  const updateArrows = useCallback(() => {
    if (!track) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    // RTL reports scrollLeft as a negative offset from the start.
    const current = Math.abs(track.scrollLeft);
    setCanPrevious(current > 4);
    setCanNext(current < maxScroll - 4);
  }, [track]);

  useEffect(() => {
    if (!track) return;

    updateArrows();
    track.addEventListener("scroll", updateArrows, { passive: true });
    const observer = new ResizeObserver(updateArrows);
    observer.observe(track);
    return () => {
      track.removeEventListener("scroll", updateArrows);
      observer.disconnect();
    };
  }, [track, updateArrows]);

  // A level change replaces the chip set without necessarily resizing the rail.
  useEffect(() => {
    updateArrows();
  }, [levelKey, visibleCategories, updateArrows]);

  const chooseCategory = (category: ShopFilterCategory) => {
    onSelect(String(category.id));
    if ((category.children?.length ?? 0) > 0) {
      setBrowsePathIds((current) => [...current, category.id]);
    }
  };

  const choosePathLevel = (index: number) => {
    if (index < 0) {
      setBrowsePathIds([]);
      onSelect(null);
      return;
    }

    const category = browsePath[index];
    if (!category) return;
    setBrowsePathIds(browsePath.slice(0, index + 1).map((item) => item.id));
    onSelect(String(category.id));
  };

  const scroll = (direction: "previous" | "next") => {
    if (!track) return;
    const amount = direction === "previous" ? -SCROLL_STEP : SCROLL_STEP;
    track.scrollBy({
      left: i18n.dir() === "rtl" ? -amount : amount,
      behavior: "smooth",
    });
  };

  if (hasFailed) return null;

  return (
    <section className="ac-shop-categories" aria-labelledby={headingId}>
      <div className="ac-shop-categories__header">
        <div>
          <span className="ac-shop-categories__eyebrow">{eyebrow}</span>
          <h2 id={headingId}>{title}</h2>
        </div>
        {selectedId ? (
          <button
            type="button"
            className="ac-shop-categories__clear"
            onClick={() => choosePathLevel(-1)}
          >
            {t("services.categoryFilter.clear")}
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="ac-shop-categories__loading">
          <Spin size="small" />
          <span>{t("services.categoryFilter.loading")}</span>
        </div>
      ) : (
        <>
          {browsePath.length > 0 ? (
            <nav
              className="ac-shop-categories__path"
              aria-label={t("services.categoryFilter.path")}
            >
              <button type="button" onClick={() => choosePathLevel(-1)}>
                {allLabel}
              </button>
              {browsePath.map((category, index) => (
                <span key={category.id}>
                  <span aria-hidden className="ac-shop-categories__path-separator">
                    /
                  </span>
                  <button
                    type="button"
                    aria-current={
                      index === browsePath.length - 1 ? "page" : undefined
                    }
                    onClick={() => choosePathLevel(index)}
                  >
                    {category.name}
                  </button>
                </span>
              ))}
            </nav>
          ) : null}

          <div className="ac-shop-categories__rail">
            <button
              type="button"
              className="ac-shop-categories__arrow"
              aria-label={t("services.categoryScrollPrev")}
              disabled={!canPrevious}
              onClick={() => scroll("previous")}
            >
              <span aria-hidden>‹</span>
            </button>

            <div
              key={levelKey}
              ref={setTrack}
              className="ac-shop-categories__track"
              role="radiogroup"
              aria-label={title}
              dir={i18n.dir()}
            >
              <button
                type="button"
                role="radio"
                aria-checked={scopeSelected}
                className={`ac-shop-category ${
                  scopeSelected ? "is-selected" : ""
                }`}
                onClick={() =>
                  currentParent
                    ? onSelect(String(currentParent.id))
                    : choosePathLevel(-1)
                }
              >
                <span className="ac-shop-category__icon" aria-hidden>
                  {renderIcon(currentParent)}
                </span>
                <span className="ac-shop-category__label">
                  {currentParent?.name ?? allLabel}
                </span>
              </button>

              {visibleCategories.map((category) => {
                const selected = selectedId === String(category.id);

                return (
                  <button
                    key={category.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`ac-shop-category ${
                      selected ? "is-selected" : ""
                    }`}
                    onClick={() => chooseCategory(category)}
                  >
                    <span className="ac-shop-category__icon" aria-hidden>
                      {renderIcon(category)}
                    </span>
                    <span className="ac-shop-category__label">
                      {category.name}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="ac-shop-categories__arrow"
              aria-label={t("services.categoryScrollNext")}
              disabled={!canNext}
              onClick={() => scroll("next")}
            >
              <span aria-hidden>›</span>
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default ShopCategoryFilter;
